
import { CreateElement, VNode, VNodeChildren } from 'vue';
import { Component, Emit, Prop, Vue } from 'vue-property-decorator';
import { SortParams, PageParams } from '../composite/tables/Table/Table.types';

interface Pagination extends Vue {
  first(): void;
  previous(): void;
  next(): void;
  last(): void;
}

/**
 * Usage:
 * <TableController
 *    @load="loadData"
 *    :items="x" :totalItems="totalX"
 *    >
 *    <template slot-scope="{ items, totalItems, sort, page, updateSort, updatePage }">
 *    ...
 *    </template>
 * </TableController>
 * load handler should set items and totalItems.
 */
@Component
export default class TableController extends Vue {
  public sort!: SortParams;
  public page!: PageParams;
  @Prop({default: []}) protected items!: any[];
  @Prop({default: 0}) protected totalItems!: number;
  @Prop({default: ''}) protected defaultSortField!: string;
  @Prop({default: false}) protected defaultSortAsc!: boolean;
  @Prop({default: -1}) protected itemsPerPage!: number;

  public data() {
    return {
      sort: {
        field: this.defaultSortField,
        asc: this.defaultSortAsc,
      },
      page: {
        offset: 0,
        limit: this.itemsPerPage,
      },
    };
  }

  public mounted() {
    this.load();
  }

  /**
   * Event for parent container to react to changes and reload data.
   * Handler should set :items and :totalItems.
   */
  @Emit()
  public load() {
    return({
      sort: this.sort,
      page: this.page,
    });
  }

  protected render(h: CreateElement): VNode {
    if (this.$scopedSlots.default) {
      return this.$scopedSlots.default!({
        items: this.items,
        totalItems: this.totalItems,
        updateSort: this.updateSort,
        updatePage: this.updatePage,
        sort: this.sort,
        page: this.page,
      }) as any;
    } else {
      return h();
    }
  }

  /**
   * Handle update sort event.
   * This function is passed to the slot scope to be passed on to Header.
   */
  protected updateSort({ field, asc }: SortParams): void {
    this.sort.field = field;
    this.sort.asc = asc;

    // If we can find the Pagination, tell it to reset to first page
    const pagination = this._findPagination() as Pagination;
    if (pagination) {
      pagination.first();
      // This will trigger updatePage and then load
      // so let's avoid triggering load twice.
    } else {
      this.load();
    }
  }

  /**
   * Handle update page event.
   * This function is passed to the slot scope to be passed on to Pagination.
   */
  protected updatePage({ limit, offset }: PageParams): void {
    this.page.limit = limit;
    this.page.offset = offset;
    this.load();
  }

  /**
   * Try to find instance of Pagination within children
   */
  protected _findPagination(): Vue | null {
    const maxDepth = 5;
    function tarverseChildren(children: Vue[], depth: number): Vue | null {
      if (depth === 0) { return null; }
      for (const child of children) {
        if (child.$options.name === 'Pagination') { return child; }
        const inChildren = tarverseChildren(child.$children, depth--);
        if (inChildren) { return inChildren; }
      }
      return null;
    }
    return tarverseChildren(this.$children, maxDepth);
  }
}
